// @workflow assistant.ui-tools, tasks.show-in-3d
/**
 * The UI tools the agent may ask the app to run: actions (client.request kind 'ui.action') and
 * queries (kind 'ui.query'). Both registries are CLOSED: only the names below exist, each checks
 * its own arguments, and nothing from the model is ever executed as code. An action name is never
 * a query and the other way round. New tools are added here one at a time.
 * No Vue or path-alias imports, so `npm run test:unit` (node --test) can load it directly.
 */
import { toAppLocation } from './markdown.js';
import { UI_TOOL, UI_TOOL_ERROR, ok, unsupported, failed, noAccess, notAvailable } from './uiToolContract.js';
import { VIEWER_ACTIONS, VIEWER_QUERIES } from './viewerTools.js';
import { TAG_ACTIONS } from './tagTools.js';
import { TASK_ACTIONS } from './taskTools.js';
import { TASK_BOARD_ACTIONS } from './taskBoardTools.js';
import { REGISTER_ACTIONS } from './registerTools.js';
import { DOCUMENT_ACTIONS } from './documentTools.js';
import { capabilityLabel } from '../../../actions/permissions.js';

export { UI_TOOL, UI_TOOL_ERROR };

/**
 * Each tool: check(args) returns the validated input or null; `capability`, when present, is the
 * app capability it needs (actions/permissions.js): after the check the runner asks `host.can`
 * and answers not_available "You don't have access to …" without preparing anything (client UX
 * only, the server stays the authority; a host without `can` allows it, the app's runner always
 * has one); ready(host, input), when present,
 * gets the page ready (it may wait) and resolves to a not_available / failed result or null;
 * run(input, host) does the work and returns the result.
 * `host` is what the app gives the tools for one request: navigate(location) → Promise, optionally
 * isPage(location) → boolean (AVA has a page there: src/router/notFound.js), `viewer`
 * (viewerTools.js), and optionally `signal` (AbortSignal: the request was stopped),
 * `progress(text|null)` (the line the user reads while a tool prepares) and `logError(name, err)`
 * (a tool that threw is answered `failed`, and logged there, never swallowed unseen).
 */
/** ui.navigate's answers in plain words (the chip toast shows them as they are). */
export const NAVIGATE_DETAIL = Object.freeze({
  NOT_FOUND: "That page doesn't exist in AVA or was removed.",
});

const ACTIONS = Object.freeze({
  [UI_TOOL.NAVIGATE]: {
    check: (args) => toAppLocation(args && args.to),
    run: async (location, host) => {
      // Never "opened" for an address with no page (a removed page, an old chip): say so instead of navigating.
      // A host that cannot tell (no isPage) just navigates.
      if (typeof host.isPage === 'function' && !host.isPage(location)) return notAvailable(NAVIGATE_DETAIL.NOT_FOUND);
      await host.navigate(location);
      return ok();
    },
  },
  ...VIEWER_ACTIONS,
  ...TAG_ACTIONS,
  ...TASK_ACTIONS,
  ...TASK_BOARD_ACTIONS,
  ...REGISTER_ACTIONS,
  ...DOCUMENT_ACTIONS,
});

const QUERIES = Object.freeze({ ...VIEWER_QUERIES });

/** What this UI build can run: sent with every message as `client.uiTools`, so the server offers only these. */
export const UI_TOOL_NAMES = Object.freeze([...Object.keys(ACTIONS), ...Object.keys(QUERIES)]);

/** The actions alone: what a button under a reply may run (a query is never a button). */
export const UI_ACTION_NAMES = Object.freeze(Object.keys(ACTIONS));

/** @param {unknown} name @returns {boolean} an action of this build (own names only, never `toString`) */
export const isUiActionName = (name) => typeof name === 'string' && Object.prototype.hasOwnProperty.call(ACTIONS, name);

/** The user may use the tool's capability (always, for a tool without one or a host without `can`). */
const mayUse = (tool, host) => !tool.capability || !host || typeof host.can !== 'function' || host.can(tool.capability);

/**
 * Whether the user may run this action, by the same capability check the runner makes: a button
 * (a reply block's ref) that would only be refused is not drawn as a button.
 * @param {{ name?: string }|null} action
 * @param {(capability: string) => boolean} canUse the user's capabilities (actions/permissions.js `can`)
 * @returns {boolean} false for an action this build does not have
 */
export function mayRunAction(action, canUse) {
  if (!action || !isUiActionName(action.name)) return false;
  return mayUse(ACTIONS[action.name], { can: canUse });
}

async function runFrom(registry, request, host) {
  const name = request && request.name;
  if (typeof name !== 'string' || !Object.prototype.hasOwnProperty.call(registry, name)) return unsupported();
  const tool = registry[name];
  const input = tool.check(request.args);
  if (input === null) return unsupported();
  try {
    if (!mayUse(tool, host)) return noAccess(capabilityLabel(tool.capability));
    const blocked = tool.ready ? await tool.ready(host, input) : null;
    return blocked || await tool.run(input, host);
  } catch (err) {
    if (host && typeof host.logError === 'function') host.logError(name, err);
    return failed();
  }
}

/**
 * @param {{ name?: string, args?: object }|null} action
 * @param {{ navigate: (location: string|object) => Promise<void>, isPage?: (location: string|object) => boolean, viewer?: object, tasks?: object, can?: (capability: string) => boolean, signal?: AbortSignal, progress?: Function }} host
 * @returns {Promise<{ ok: true, data?: object } | { ok: false, error: string, detail?: string }>}
 */
export function runUiAction(action, host) {
  return runFrom(ACTIONS, action, host);
}

/**
 * @param {{ name?: string, args?: object }|null} query
 * @param {{ viewer?: object }} host
 * @returns {Promise<{ ok: true, data: object } | { ok: false, error: string, detail?: string }>}
 */
export function runUiQuery(query, host) {
  return runFrom(QUERIES, query, host);
}
